import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PageProgressNavigator.css';

const SECTIONS = [
  { id: 'home',     label: 'Hero'         },
  { id: 'journey',  label: 'How It Works' },
  { id: 'services', label: 'Services'     },
  { id: 'clients',  label: 'Clients'      },
  { id: 'cta',      label: 'Get Started'  },
];

const PageProgressNavigator = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [scrollPct, setScrollPct] = useState(0);
  const [visible, setVisible] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('up');
  const lastScrollY = useRef(0);

  const handleScroll = useCallback(() => {
    const scrollTop  = window.scrollY;
    setVisible(scrollTop > 300);

    // Track scroll direction
    if (scrollTop > lastScrollY.current) {
      setScrollDirection('down');
    } else if (scrollTop < lastScrollY.current) {
      setScrollDirection('up');
    }
    lastScrollY.current = scrollTop <= 0 ? 0 : scrollTop;

    // Get offsets of all sections in the document
    const offsets = SECTIONS.map(s => {
      const el = document.getElementById(s.id);
      if (el) {
        return el.getBoundingClientRect().top + window.scrollY;
      }
      return 0;
    });

    // Interpolate scootie position between the corresponding checkpoint dots
    let scootyPct = 0;
    const n = SECTIONS.length;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    
    if (scrollTop <= offsets[0]) {
      scootyPct = 0;
    } else if (scrollTop >= offsets[n - 1] || scrollTop >= maxScroll) {
      scootyPct = 100;
    } else {
      for (let i = 0; i < n - 1; i++) {
        const startOffset = offsets[i];
        const endOffset = offsets[i + 1];
        if (scrollTop >= startOffset && scrollTop <= endOffset) {
          const range = endOffset - startOffset;
          const frac = range > 0 ? (scrollTop - startOffset) / range : 0;
          const step = 100 / (n - 1);
          scootyPct = (i + frac) * step;
          break;
        }
      }
    }
    setScrollPct(scootyPct);

    // Determine active section by IntersectionObserver-style logic
    let current = 'home';
    for (const section of SECTIONS) {
      const el = document.getElementById(section.id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.45) {
          current = section.id;
        }
      }
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      className={`page-nav ${visible ? 'page-nav--visible' : ''}`}
      aria-label="Page section navigator"
    >
      {/* Vertical progress track */}
      <div className="page-nav__track">
        <div
          className="page-nav__progress"
          style={{ height: `${scrollPct}%` }}
          aria-hidden="true"
        />
      </div>

      {/* Moving Scootie Element */}
      <div
        className={`page-nav__scootie ${scrollDirection === 'down' ? 'page-nav__scootie--down' : ''}`}
        style={{ top: `${scrollPct}%` }}
        aria-hidden="true"
      >
        <img src="/scootie-scroll-element.png" alt="scootie scroll marker" />
      </div>

      {/* Section dots */}
      <ul className="page-nav__dots">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <button
              className={`page-nav__dot ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => scrollTo(s.id)}
              aria-label={`Navigate to ${s.label}`}
              title={s.label}
            >
              <span className="page-nav__dot-inner" />
              <span className="page-nav__label">{s.label}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* Scroll to top */}
      {scrollPct > 80 && (
        <button
          className="page-nav__top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
          title="Back to Top"
        >
          ↑
        </button>
      )}
    </nav>
  );
};

export default PageProgressNavigator;
